/**
 * RightPanel - Reusable overlay/drawer panel for detail views
 *
 * A standalone component that can be used outside of PageLayout
 * for cases where the detail panel needs to be managed separately
 * (like IssueDetailPanel).
 *
 * Features:
 * - Overlay mode with backdrop
 * - Drawer mode with rounded corners
 * - Inline mode (no overlay)
 * - Responsive: full-screen on mobile
 * - Focus trap and escape key handling
 * - Smooth animations
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useIsMobile } from '../../hooks/useMediaQuery';

export interface RightPanelProps {
  children: ReactNode;

  /** Whether the panel is open */
  isOpen: boolean;

  /** Callback when panel should close */
  onClose: () => void;

  /** Panel display mode */
  mode?: 'overlay' | 'drawer' | 'inline';

  /** Panel width in pixels (default: 400) */
  width?: number;

  /** Panel title for header */
  title?: string;

  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;

  /** Whether to show header (default: true if title provided) */
  showHeader?: boolean;

  /** Header actions slot */
  headerActions?: ReactNode;

  /** Whether clicking backdrop closes panel (default: true) */
  closeOnBackdropClick?: boolean;

  /** Whether pressing Escape closes panel (default: true) */
  closeOnEscape?: boolean;

  /** Additional className for the panel */
  className?: string;

  /** z-index for the panel (default: 50) */
  zIndex?: number;
}

export function RightPanel({
  children,
  isOpen,
  onClose,
  mode = 'overlay',
  width = 400,
  title,
  showCloseButton = true,
  showHeader,
  headerActions,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  zIndex = 50,
}: RightPanelProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);

  // Determine if header should be shown
  const shouldShowHeader = showHeader ?? !!title;

  // Focus trap for accessibility
  const focusTrapRef = useFocusTrap<HTMLDivElement>({
    enabled: isOpen,
    onEscape: closeOnEscape ? onClose : undefined,
    restoreFocus: true,
    initialFocus: true,
  });

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen && (mode === 'overlay' || mode === 'drawer')) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  // Mobile: always full-screen
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          style={{ zIndex: zIndex - 1 }}
          onClick={closeOnBackdropClick ? onClose : undefined}
          aria-hidden="true"
        />

        {/* Full-screen panel */}
        <div
          ref={focusTrapRef}
          className={`fixed inset-0 bg-white flex flex-col ${className}`}
          style={{ zIndex }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'right-panel-title' : undefined}
        >
          {/* Mobile header with back button */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-line bg-white">
            <button
              onClick={onClose}
              className="p-2 -ml-2 rounded-lg text-muted hover:bg-surface-subtle transition-colors"
              aria-label="Close panel"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {title && (
              <h2 id="right-panel-title" className="text-lg font-semibold text-text flex-1">
                {title}
              </h2>
            )}
            {headerActions}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </>
    );
  }

  // Inline mode: no backdrop, just the panel
  if (mode === 'inline') {
    return (
      <div
        ref={focusTrapRef}
        className={`flex flex-col bg-white border-l border-line ${className}`}
        style={{ width }}
        role="complementary"
        aria-labelledby={title ? 'right-panel-title' : undefined}
      >
        {shouldShowHeader && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            {title && (
              <h2 id="right-panel-title" className="text-sm font-semibold text-text">
                {title}
              </h2>
            )}
            <div className="flex items-center gap-2">
              {headerActions}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-surface-subtle text-subtle hover:text-muted transition-colors"
                  aria-label="Close panel"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    );
  }

  // Overlay/Drawer mode
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 transition-opacity"
        style={{ zIndex: zIndex - 1 }}
        onClick={closeOnBackdropClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={focusTrapRef}
        className={`
          fixed inset-y-0 right-0 bg-white shadow-2xl flex flex-col
          transform transition-transform duration-300 ease-out
          ${mode === 'drawer' ? 'rounded-l-2xl' : ''}
          ${className}
        `}
        style={{ width, zIndex }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'right-panel-title' : undefined}
      >
        {shouldShowHeader && (
          <div
            className={`
              flex items-center justify-between px-4 py-3 border-b border-line
              ${mode === 'drawer' ? 'rounded-tl-2xl' : ''}
            `}
          >
            {title && (
              <h2 id="right-panel-title" className="text-lg font-semibold text-text">
                {title}
              </h2>
            )}
            <div className="flex items-center gap-2">
              {headerActions}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-surface-subtle text-subtle hover:text-muted transition-colors"
                  aria-label="Close panel"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
}

export default RightPanel;
